import React from 'react';
import { CheckSquare, AlertCircle, Award } from 'lucide-react';

/**
 * Секция качественных факторов для оценки риска банкротства
 */
const QualitativeFactorsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  if (!formData.includeQualitativeFactors) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
            <Award className="w-5 h-5 mr-2 text-orange-500" />
            Качественные факторы
          </h3>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="includeQualitativeFactors"
              name="includeQualitativeFactors"
              checked={formData.includeQualitativeFactors}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="includeQualitativeFactors" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать в оценке
            </label>
          </div>
        </div>
        
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Качественные факторы позволяют учесть нефинансовые аспекты бизнеса, которые могут влиять на риск банкротства. Включите их для более комплексной оценки.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
          <Award className="w-5 h-5 mr-2 text-orange-500" />
          Качественные факторы
        </h3>
        
        <div className="flex items-center">
          <input
            type="checkbox"
            id="includeQualitativeFactors"
            name="includeQualitativeFactors"
            checked={formData.includeQualitativeFactors}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <label htmlFor="includeQualitativeFactors" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Учитывать в оценке
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Кредитная история */}
        <div>
          <label htmlFor="creditHistory" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Кредитная история
          </label>
          <select
            id="creditHistory"
            name="creditHistory"
            value={formData.creditHistory}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.creditHistory ? 'input-error' : ''}`}
          >
            <option value="excellent">Отличная</option>
            <option value="good">Хорошая</option>
            <option value="average">Средняя</option>
            <option value="poor">Плохая</option>
            <option value="very_poor">Очень плохая</option>
          </select>
          {errors.creditHistory && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.creditHistory}
            </p>
          )}
        </div>

        {/* Отношения с поставщиками */}
        <div>
          <label htmlFor="supplierRelationships" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Отношения с поставщиками
          </label>
          <select
            id="supplierRelationships"
            name="supplierRelationships"
            value={formData.supplierRelationships}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.supplierRelationships ? 'input-error' : ''}`}
          >
            <option value="excellent">Отличные</option>
            <option value="good">Хорошие</option>
            <option value="average">Средние</option>
            <option value="problematic">Проблемные</option>
            <option value="critical">Критические</option>
          </select>
          {errors.supplierRelationships && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.supplierRelationships}
            </p>
          )}
        </div>

        {/* Задержки платежей */}
        <div>
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Задержки платежей
          </div>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="delayedPayments"
              checked={formData.delayedPayments}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Есть систематические задержки платежей
            </span>
          </label>
        </div>

        {/* Судебные иски */}
        <div>
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Судебные иски
          </div>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="pendingLawsuits"
              checked={formData.pendingLawsuits}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Имеются существенные судебные иски
            </span>
          </label>
        </div>

        {/* Изменения в руководстве */}
        <div>
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Изменения в руководстве
          </div>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="managementChanges"
              checked={formData.managementChanges}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Недавние существенные изменения в руководстве
            </span>
          </label>
        </div>
      </div>

      {/* Информация о влиянии факторов */}
      <div className="mt-4 bg-orange-100 dark:bg-orange-900/30 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-1">
              Важность нефинансовых факторов
            </h4>
            <p className="text-xs text-orange-600 dark:text-orange-400">
              Качественные факторы могут значительно влиять на риск банкротства. Исследования показывают, что 
              негативные отношения с поставщиками, судебные иски и частая смена руководства являются распространенными
              предшественниками финансовых проблем. Их учет делает оценку более точной.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualitativeFactorsSection;